import { useCallback, useEffect, useMemo, useState, type FC } from "react";
import { LeftOutlined } from "@ant-design/icons";
import { message } from "antd";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import { Button, DataTable, Form, StatusBadge } from "../../shared";
import { AppStrings } from "../../../utils/constants/AppStrings";
import { formatString } from "../../../utils/constants/stringUtils";
import { useComplianceRules } from "../../../queries/regulator-portal/compliance/useComplianceRules";
import { useUpdateComplianceRule } from "../../../queries/regulator-portal/compliance/useUpdateComplianceRule";
import {
  complianceRuleFromLocationState,
  formatDbConfigConditions,
  normalizeComplianceRule,
} from "../../../queries/regulator-portal/compliance/parseComplianceRule";
import type {
  ComplianceMonitoredDbConfig,
  ComplianceMonitoredWorkload,
  ComplianceRule,
} from "../../../queries/regulator-portal/compliance/types";
import {
  buildRegulatorPortalRulePath,
  decodeRegulatorPortalPathId,
  REGULATOR_PORTAL_RULES_ROUTE,
} from "../regulatorPortalRoutes";
import { portalComplianceErrorMessage } from "./portalComplianceError";
import RegulatorPortalRuleForm from "./RegulatorPortalRuleForm";
import {
  formValuesToWritePayload,
  ruleToFormValues,
  type RuleFormValues,
} from "./ruleFormData";

import styles from "./Compliance.module.css";

const emptyValue = (value: string) => value || AppStrings.EMPTY_REASON;

type GameStopRow = {
  name: string;
  resources: string[];
};

const gameStopRows = (mapping: Record<string, string[]>): GameStopRow[] =>
  Object.entries(mapping).map(([name, resources]) => ({ name, resources }));

const RegulatorPortalRuleDetail: FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { ruleId = "" } = useParams<{ ruleId: string }>();
  const decodedId = decodeRegulatorPortalPathId(ruleId);
  const [form] = Form.useForm<RuleFormValues>();
  const updateRule = useUpdateComplianceRule();
  const [isEditing, setIsEditing] = useState(false);
  const [savedRule, setSavedRule] = useState<ComplianceRule | null>(null);
  const ruleFromState = useMemo(
    () => complianceRuleFromLocationState(location.state, decodedId),
    [location.state, decodedId],
  );
  const rulesQuery = useComplianceRules(
    savedRule == null && ruleFromState == null && Boolean(decodedId),
  );
  const ruleFromList = useMemo(
    () =>
      (rulesQuery.data ?? []).find((row) => row.id === decodedId) ?? null,
    [rulesQuery.data, decodedId],
  );
  const rule: ComplianceRule | null = savedRule ?? ruleFromState ?? ruleFromList;
  const loadError =
    savedRule == null &&
    ruleFromState == null &&
    rulesQuery.isError &&
    !rule
      ? portalComplianceErrorMessage(
          rulesQuery.error,
          AppStrings.PAM_REGULATOR_PORTAL_RULE_LOAD_ERROR,
        )
      : null;
  const isLoading =
    savedRule == null &&
    ruleFromState == null &&
    rulesQuery.isLoading &&
    ruleFromList == null;
  const stops = rule ? gameStopRows(rule.indicatorMapping) : [];

  useEffect(() => {
    setSavedRule(null);
    setIsEditing(false);
  }, [decodedId]);

  const handleStartEdit = useCallback(() => {
    if (!rule) {
      return;
    }
    form.setFieldsValue(ruleToFormValues(rule));
    setIsEditing(true);
  }, [form, rule]);

  const handleCancelEdit = useCallback(() => {
    if (rule) {
      form.setFieldsValue(ruleToFormValues(rule));
    }
    setIsEditing(false);
  }, [form, rule]);

  const handleFinish = useCallback(
    async (values: RuleFormValues) => {
      if (!rule) {
        return;
      }
      const payload = formValuesToWritePayload(values, rule);
      try {
        const saved = await updateRule.mutateAsync(payload);
        const next =
          saved ??
          normalizeComplianceRule({
            ...payload,
            regulator: rule.regulator,
            client: rule.client,
          });
        if (next) {
          setSavedRule(next);
          navigate(buildRegulatorPortalRulePath(next.id), {
            replace: true,
            state: { rule: next },
          });
        }
        setIsEditing(false);
        message.success(AppStrings.PAM_REGULATOR_PORTAL_RULE_SAVE_SUCCESS);
      } catch (error) {
        message.error(
          portalComplianceErrorMessage(
            error,
            AppStrings.PAM_REGULATOR_PORTAL_RULE_SAVE_ERROR,
          ),
        );
      }
    },
    [navigate, rule, updateRule],
  );

  const workloadColumns = useMemo(
    () => [
      {
        title: AppStrings.PAM_REGULATOR_PORTAL_RESOURCE_TYPE,
        dataIndex: "resourceType",
        key: "resourceType",
        render: (value: string) => emptyValue(value),
      },
      {
        title: AppStrings.PAM_REGULATOR_PORTAL_REGION,
        dataIndex: "region",
        key: "region",
        render: (value: string) => emptyValue(value),
      },
      {
        title: AppStrings.PAM_REGULATOR_PORTAL_CLUSTER,
        dataIndex: "clusterName",
        key: "clusterName",
        render: (value: string) => emptyValue(value),
      },
      {
        title: AppStrings.PAM_REGULATOR_PORTAL_NAMESPACE,
        dataIndex: "namespace",
        key: "namespace",
        render: (value: string) => emptyValue(value),
      },
      {
        title: AppStrings.PAM_REGULATOR_PORTAL_DEPLOYMENT,
        dataIndex: "deployment",
        key: "deployment",
        render: (value: string) => emptyValue(value),
      },
    ],
    [],
  );

  const gameStopColumns = useMemo(
    () => [
      {
        title: AppStrings.PAM_REGULATOR_PORTAL_GAME_STOPS,
        dataIndex: "name",
        key: "name",
        render: (value: string) => emptyValue(value),
      },
      {
        title: AppStrings.PAM_REGULATOR_PORTAL_RESOURCES,
        dataIndex: "resources",
        key: "resources",
        ellipsis: false,
        render: (resources: string[]) =>
          resources?.length ? (
            <div className={styles.tags}>
              {resources.map((resource) => (
                <StatusBadge key={resource} tone="info" size="sm">
                  {resource}
                </StatusBadge>
              ))}
            </div>
          ) : (
            AppStrings.EMPTY_REASON
          ),
      },
    ],
    [],
  );

  const dbConfigColumns = useMemo(
    () => [
      {
        title: AppStrings.PAM_REGULATOR_PORTAL_TABLE,
        dataIndex: "tableName",
        key: "tableName",
        render: (value: string) => emptyValue(value),
      },
      {
        title: AppStrings.PAM_REGULATOR_PORTAL_REGION,
        dataIndex: "region",
        key: "region",
        render: (value: string) => emptyValue(value),
      },
      {
        title: AppStrings.PAM_REGULATOR_PORTAL_FIELDS,
        dataIndex: "fields",
        key: "fields",
        ellipsis: false,
        render: (fields: string[]) =>
          fields?.length ? (
            <div className={styles.tags}>
              {fields.map((field) => (
                <StatusBadge key={field} tone="info" size="sm">
                  {field}
                </StatusBadge>
              ))}
            </div>
          ) : (
            AppStrings.EMPTY_REASON
          ),
      },
      {
        title: AppStrings.PAM_REGULATOR_PORTAL_CONDITIONS,
        dataIndex: "conditions",
        key: "conditions",
        render: (conditions: Record<string, string>) =>
          emptyValue(formatDbConfigConditions(conditions ?? {})),
      },
    ],
    [],
  );

  return (
    <section className={styles.page} data-testid="regulator-portal-rule-detail">
      <div className={styles.header}>
        <button
          type="button"
          className={styles.backButton}
          aria-label={AppStrings.PAM_REGULATOR_PORTAL_BACK_TO_RULES}
          onClick={() => navigate(REGULATOR_PORTAL_RULES_ROUTE)}
        >
          <LeftOutlined className={styles.backButtonIcon} />
        </button>
        <h1 className={styles.title}>
          {rule?.client || AppStrings.PAM_REGULATOR_PORTAL_RULE_DETAIL}
        </h1>
        {rule?.regulator ? (
          <StatusBadge
            tone="neutral"
            size="sm"
            data-testid="regulator-portal-rule-regulator"
          >
            {AppStrings.PAM_REGULATOR_PORTAL_REGULATOR_NAME}: {rule.regulator}
          </StatusBadge>
        ) : null}
        {rule && !isLoading ? (
          <div className={styles.headerActions}>
            {isEditing ? (
              <>
                <Button
                  htmlType="button"
                  intent="neutral"
                  variant="outline"
                  onClick={handleCancelEdit}
                  disabled={updateRule.isPending}
                  data-testid="regulator-portal-rule-cancel"
                >
                  {AppStrings.CANCEL}
                </Button>
                <Button
                  htmlType="button"
                  onClick={() => form.submit()}
                  loading={updateRule.isPending}
                  data-testid="regulator-portal-rule-save"
                >
                  {AppStrings.PAM_REGULATOR_PORTAL_SAVE_RULE}
                </Button>
              </>
            ) : (
              <Button
                htmlType="button"
                onClick={handleStartEdit}
                data-testid="regulator-portal-rule-edit"
              >
                {AppStrings.PAM_REGULATOR_PORTAL_EDIT_RULE}
              </Button>
            )}
          </div>
        ) : null}
      </div>

      {isLoading ? (
        <p className={styles.message}>{AppStrings.PAM_REGULATOR_PORTAL_LOADING}</p>
      ) : null}
      {loadError ? (
        <p className={styles.message} role="alert">
          {loadError}
        </p>
      ) : null}
      {!isLoading && !rule && !loadError ? (
        <p className={styles.message}>
          {AppStrings.PAM_REGULATOR_PORTAL_RULE_NOT_FOUND}
        </p>
      ) : null}

      {!isLoading && rule && isEditing ? (
        <RegulatorPortalRuleForm
          form={form}
          initialValues={ruleToFormValues(rule)}
          onFinish={handleFinish}
        />
      ) : null}

      {!isLoading && rule && !isEditing ? (
        <>
          <dl className={styles.details}>
            <div>
              <dt>{AppStrings.PAM_REGULATOR_PORTAL_CLIENT}</dt>
              <dd>{emptyValue(rule.client)}</dd>
            </div>
            <div>
              <dt>{AppStrings.PAM_REGULATOR_PORTAL_REGULATOR_NAME}</dt>
              <dd>{emptyValue(rule.regulator)}</dd>
            </div>
            <div>
              <dt>{AppStrings.DESCRIPTION}</dt>
              <dd>{emptyValue(rule.description)}</dd>
            </div>
            <div>
              <dt>{AppStrings.PAM_REGULATOR_PORTAL_TARGET_ACCOUNT}</dt>
              <dd>{emptyValue(rule.targetAccountId)}</dd>
            </div>
            <div>
              <dt>{AppStrings.PAM_REGULATOR_PORTAL_BASELINE_ACCOUNT}</dt>
              <dd>{emptyValue(rule.baselineAccountId)}</dd>
            </div>
          </dl>

          <div className={styles.detailSection}>
            <div className={styles.detailSectionHeader}>
              <h2 className={styles.detailSectionTitle}>
                {formatString(AppStrings.PAM_REGULATOR_PORTAL_MONITORED_WORKLOADS_TITLE, {
                  count: String(rule.monitoredWorkloads.length),
                })}
              </h2>
            </div>
            {rule.monitoredWorkloads.length > 0 ? (
              <DataTable<ComplianceMonitoredWorkload>
                rowKey={(_row, index) =>
                  [
                    _row.resourceType,
                    _row.region,
                    _row.clusterName,
                    _row.namespace,
                    _row.deployment,
                    index,
                  ]
                    .filter(Boolean)
                    .join(":")
                }
                columns={workloadColumns}
                dataSource={rule.monitoredWorkloads}
                pagination={false}
                data-testid="regulator-portal-rule-workloads"
              />
            ) : (
              <p className={styles.message}>
                {AppStrings.PAM_REGULATOR_PORTAL_WORKLOADS_EMPTY}
              </p>
            )}
          </div>

          <div className={styles.detailSection}>
            <div className={styles.detailSectionHeader}>
              <h2 className={styles.detailSectionTitle}>
                {formatString(AppStrings.PAM_REGULATOR_PORTAL_GAME_STOPS_TITLE, {
                  count: String(stops.length),
                })}
              </h2>
            </div>
            {stops.length > 0 ? (
              <DataTable<GameStopRow>
                rowKey="name"
                columns={gameStopColumns}
                dataSource={stops}
                pagination={false}
                data-testid="regulator-portal-rule-game-stops"
              />
            ) : (
              <p className={styles.message}>
                {AppStrings.PAM_REGULATOR_PORTAL_GAME_STOPS_EMPTY}
              </p>
            )}
          </div>

          <div className={styles.detailSection}>
            <div className={styles.detailSectionHeader}>
              <h2 className={styles.detailSectionTitle}>
                {formatString(AppStrings.PAM_REGULATOR_PORTAL_MONITORED_DB_CONFIGS_TITLE, {
                  count: String(rule.monitoredDbConfigs.length),
                })}
              </h2>
            </div>
            {rule.monitoredDbConfigs.length > 0 ? (
              <DataTable<ComplianceMonitoredDbConfig>
                rowKey={(row, index) => `${row.tableName}:${row.region}:${index}`}
                columns={dbConfigColumns}
                dataSource={rule.monitoredDbConfigs}
                pagination={false}
                data-testid="regulator-portal-rule-db-configs"
              />
            ) : (
              <p className={styles.message}>
                {AppStrings.PAM_REGULATOR_PORTAL_DB_CONFIGS_EMPTY}
              </p>
            )}
          </div>
        </>
      ) : null}
    </section>
  );
};

export default RegulatorPortalRuleDetail;
